import React, { useCallback } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { LinearGradient } from 'expo-linear-gradient';
import { useDispatch, useSelector } from 'react-redux';
import PressableStyled from '../PressableStyled';
import BackArrow from '../BackIcon';
import Colors from '../../constants/Colors';
import DateComponent from '../DateComponent';
import { DateRange, DateSelectionRange, updateDateRange } from '../../redux/dateRangeSlice';
import HomeGraphLineSelectorMobile from './HomeGraphLineSelectorMobile';
import HappyDetailsGraphMobile from '../../graphs/HappyDetailsGraphMobile';

interface Props {
  goBack: any;
  score: number;
  tileData: any;
  setTileData: any;
  graphData: any;
  isDev: boolean;
}

const getHappyColor = (target) => {
  if (target?.value < 0.5) return Colors.light.hereworksPink;
  if (target?.value < 0.8) return Colors.light.hereworksOrange;
  return Colors.light.hereworksTeal;
};

const getHappyTextColor = (target) => {
  if (target?.value < 0.5) return Colors.light.hereworksPink;
  if (target?.value < 0.8) return Colors.light.hereworksOrange;
  return Colors.light.hereworksExtraDarkTeal;
};

const HappyDetailsMobile = ({
  goBack, score, tileData, setTileData, graphData,
}: Props) => {
  const dispatch = useDispatch();
  const range: DateRange = useSelector((state: { dateRange: DateRange }) => (state.dateRange));

  const setRange = (dates) => dispatch(updateDateRange(dates));
  const onChange = useCallback(
    (start: string, end: string, selectedRange: DateSelectionRange) => setRange(
      { start, end, selectedRange },
    ),
    [setRange],
  );

  return (<SafeAreaView style={styles.container}>
    <View style={{ flexDirection: 'row', alignItems: 'center' }}>
      <PressableStyled onPress={goBack}>
        <BackArrow />
      </PressableStyled>
      <Text style={{
        fontSize: 22,
        lineHeight: 33,
        fontFamily: 'Poppins_600SemiBold',
        flex: 1,
        marginLeft: 12,
      }}>Happy score</Text>
      <Text style={[styles.score, { color: getHappyTextColor(score) }]}>
        {score}
      </Text>
    </View>
    <View style={{
      marginTop: 12,
      marginBottom: 20,
      borderRadius: 10,
      height: 12,
      width: '100%',
      backgroundColor: Colors.light.hereworksPrimaryGrey600,
    }}>
      <LinearGradient
        style={{
          width: '100%',
          height: '100%',
          flex: 1,
          position: 'absolute',
          borderRadius: 10,
        }}
        colors={[Colors.light.hereworksPrimaryGrey600, Colors.light.hereworksPrimaryGrey200]}
      />
      <LinearGradient
        style={{
          width: `${score ?? 0}%`,
          backgroundColor: '#fff',
          flex: 1,
          borderRadius: 10,
        }}
        colors={[`${getHappyColor(score)}70`, getHappyColor(score)]}
      />
    </View>
    <View style={{ backgroundColor: '#fff', flex: 1, borderRadius: 12 }}>
      <View style={styles.datePicker}>
        <DateComponent
          selectedRange={range.selectedRange}
          hideCalendarIcon
          {...{ onChange }}
          onOpen={() => { }}
          onConfirm={() => { }}
          onCompareChange={() => { }}
          startDate={range.start}
          endDate={range.end}
        />
      </View>
      <View style={styles.graph}>
        <Text style={styles.historyTitle}>Happy score vs</Text>
        <View style={{ marginTop: 8 }}>
          <HomeGraphLineSelectorMobile
            data={tileData}
            setData={setTileData}
          />
        </View>
        <HappyDetailsGraphMobile
          graphData={graphData}
          linesShown={tileData.map((tile: { lineShown: boolean }) => tile.lineShown)}
        />
      </View>
    </View>
  </SafeAreaView>);
};

const styles = StyleSheet.create({
  container: { backgroundColor: Colors.light.hereworksBackground, flex: 1, paddingHorizontal: 24 },
  graph: {
    borderTopWidth: 1.5,
    borderTopColor: Colors.light.hereworksWhite500,
    paddingTop: 16,
    flex: 1,
    marginTop: 70,
  },
  score: {
    fontSize: 22,
    lineHeight: 33,
    fontFamily: 'Poppins_600SemiBold',
  },
  datePicker: {
    zIndex: 2,
    position: 'absolute',
    width: '100%',
    top: 4,
  },
  historyTitle: {
    fontSize: 18,
    lineHeight: 27,
    fontFamily: 'Poppins_400Regular',
    color: Colors.light.hereworksBlack600,
    marginLeft: 16,
  },
});

export default HappyDetailsMobile;
